import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useWalkthrough } from "../utils/WalkthroughProvider";
import i18n from "../translationUtils/i18n";
import demoImage from "../res/Spark.png";

const BRAND_RED = "#D22630";

function DemoTile() {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 1,
        mb: 1,
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.default,
      }}
    >
      <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "center", py: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: BRAND_RED,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LocalFireDepartmentIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {i18n.t("wt_demo_event_title")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {i18n.t("wt_demo_event_subtitle")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {i18n.t("wt_demo_event_time")}
          </Typography>
        </Box>
        <Box
          component="img"
          src={demoImage}
          alt={i18n.t("wt_demo_event_title")}
          sx={{
            width: 48,
            height: 48,
            objectFit: "cover",
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
      </CardContent>
    </Card>
  );
}

function DemoZoomImage() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        sx={{
          mt: 1,
          mb: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={demoImage}
          alt={i18n.t("wt_demo_event_title")}
          onClick={() => setOpen(true)}
          sx={{
            width: "100%",
            maxWidth: 280,
            maxHeight: 180,
            objectFit: "contain",
            borderRadius: 1,
            cursor: "zoom-in",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        />
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <Box sx={{ position: "relative", bgcolor: "#000" }}>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.4)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={demoImage}
            alt={i18n.t("wt_demo_event_title")}
            sx={{
              display: "block",
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}

function SettingsBullets() {
  const items = [
    i18n.t("wt_settings_item1"),
    i18n.t("wt_settings_item2"),
    i18n.t("wt_settings_item3"),
    i18n.t("wt_settings_item4"),
    i18n.t("wt_settings_item5"),
  ];
  return (
    <>
      <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
        {items.map((it, idx) => (
          <li key={idx}>
            <Typography variant="body2">{it}</Typography>
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
        {i18n.t("wt_settings_whatsapp_note")}
      </Typography>
    </>
  );
}

export default function WalkthroughOverlay() {
  const {
    isActive,
    step,
    stepIndex,
    stepCount,
    isFirst,
    isLast,
    next,
    prev,
    skip,
  } = useWalkthrough();
  const theme = useTheme();

  if (!isActive || !step) return null;

  const titleKey = `wt_${step.key}_title`;
  const bodyKey = `wt_${step.key}_body`;

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: (theme) => theme.zIndex.modal + 10,
        px: 2,
        pb: 3,
      }}
    >
      <Card
        elevation={0}
        sx={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 480,
          bgcolor: theme.palette.background.paper,
          border: `2px solid ${theme.palette.divider}`,
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {i18n.t("wt_step", { current: stepIndex + 1, total: stepCount })}
            </Typography>
            {false && <CircularProgress size={14} sx={{ color: BRAND_RED }} />}
          </Stack>

          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {i18n.t(titleKey)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {i18n.t(bodyKey)}
          </Typography>

          {step.demo === "tile" && <DemoTile />}
          {step.demo === "image" && <DemoZoomImage />}
          {step.bullets && <SettingsBullets />}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <Button
              onClick={skip}
              size="small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {i18n.t("wt_skip")}
            </Button>

            <Stack direction="row" spacing={1}>
              {!isFirst && (
                <Button onClick={prev} variant="outlined" size="small">
                  {i18n.t("wt_previous")}
                </Button>
              )}
              <Button
                onClick={next}
                variant="contained"
                size="small"
                sx={{
                  bgcolor: BRAND_RED,
                  "&:hover": { bgcolor: "#a31d25" },
                }}
              >
                {isLast ? i18n.t("wt_allset") : i18n.t("wt_next")}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
